import { MCP_OAUTH_CHANNEL } from "./mcp-oauth-channel";
import type { RpcClient } from "./rpc";
import { rpc } from "./rpc";

export { MCP_OAUTH_CHANNEL } from "./mcp-oauth-channel";

const MCP_OAUTH_TIMEOUT_MS = 2 * 60 * 1000;
// Per tab, so it survives this tab's trip to the provider and back and nothing else.
const MCP_OAUTH_RETURN_KEY = "hrokbot-mcp-oauth-return";

/** One full-tab sign-in: the OAuth session (its state) it started, for which server, and where to return. */
type McpOauthReturn = { serverId: string; sessionId: string; returnTo: string; completed: boolean };

function readMcpOauthReturn(): McpOauthReturn | undefined {
  try {
    const value: unknown = JSON.parse(
      window.sessionStorage.getItem(MCP_OAUTH_RETURN_KEY) ?? "null",
    );
    if (!value || typeof value !== "object") return undefined;
    const serverId = "serverId" in value ? value.serverId : undefined;
    const sessionId = "sessionId" in value ? value.sessionId : undefined;
    const returnTo = "returnTo" in value ? value.returnTo : undefined;
    if (typeof serverId !== "string" || typeof sessionId !== "string" || !sessionId)
      return undefined;
    // Only a path inside this app: never another origin or a protocol-relative URL.
    if (typeof returnTo !== "string") return undefined;
    if (!returnTo.startsWith("/") || returnTo.startsWith("//") || returnTo.startsWith("/\\")) {
      return undefined;
    }
    return {
      serverId,
      sessionId,
      returnTo,
      completed: "completed" in value && value.completed === true,
    };
  } catch {
    return undefined;
  }
}

function writeMcpOauthReturn(value: McpOauthReturn | undefined) {
  try {
    if (value) window.sessionStorage.setItem(MCP_OAUTH_RETURN_KEY, JSON.stringify(value));
    else window.sessionStorage.removeItem(MCP_OAUTH_RETURN_KEY);
  } catch {
    // Storage can be unavailable; the callback then falls back to the app home.
  }
}

/**
 * After a full-tab sign-in (popup blocked) that the server accepted, mark it finished and return
 * the chat path the person left from. Only the sign-in this tab started counts: `state` must be
 * its OAuth session. The server's complete call already tied that session to this account.
 */
export function completeMcpOauthReturn(state: string): string | undefined {
  const pending = readMcpOauthReturn();
  if (!pending || pending.sessionId !== state) return undefined;
  writeMcpOauthReturn({ ...pending, completed: true });
  return pending.returnTo;
}

/** Whether this server's full-tab sign-in just finished; true only once. */
export function takeCompletedMcpOauth(serverId: string): boolean {
  const pending = readMcpOauthReturn();
  if (!pending?.completed || pending.serverId !== serverId) return false;
  writeMcpOauthReturn(undefined);
  return true;
}

export type McpOauthResult =
  | "connected"
  | "cancelled"
  | "already_connected"
  | "authorization_not_requested";

/** Run the browser OAuth popup flow for an MCP server: request an
 * authorization URL, open the popup, and wait until the callback page
 * broadcasts completion or the popup is closed without finishing.
 *
 * The BroadcastChannel (not window.opener) is the completion signal because
 * provider login pages with COOP sever the opener link. */
/** Where providers send the user back; a pre-registered client must allow it. */
export function mcpOAuthRedirectUri(): string {
  return `${window.location.origin}/mcp/oauth/callback`;
}

export async function connectMcpOauth(
  serverId: string,
  client: RpcClient = rpc,
): Promise<McpOauthResult> {
  const started = await client.mcp.oauth.begin({ serverId, redirectUri: mcpOAuthRedirectUri() });
  if (started.status !== "authorization_required") return started.status;
  const popup = window.open(
    started.authorizationUrl,
    MCP_OAUTH_CHANNEL,
    "popup,width=560,height=720",
  );
  if (!popup) {
    // Popup blocked: navigate this tab instead. The callback page returns to this chat, where the
    // card finishes the approval.
    const { pathname, search, hash } = window.location;
    writeMcpOauthReturn({
      serverId,
      sessionId: started.sessionId,
      returnTo: `${pathname}${search}${hash}`,
      completed: false,
    });
    window.location.assign(started.authorizationUrl);
    return "cancelled";
  }
  return await new Promise<McpOauthResult>((resolve) => {
    const channel = new BroadcastChannel(MCP_OAUTH_CHANNEL);
    let settled = false;
    let pollTimer = 0;
    let timeoutTimer = 0;
    const finish = (result: McpOauthResult) => {
      if (settled) return;
      settled = true;
      window.clearInterval(pollTimer);
      window.clearTimeout(timeoutTimer);
      channel.close();
      resolve(result);
    };
    pollTimer = window.setInterval(() => {
      if (!popup.closed) return;
      finish("cancelled");
    }, 500);
    timeoutTimer = window.setTimeout(() => {
      popup.close();
      finish("cancelled");
    }, MCP_OAUTH_TIMEOUT_MS);
    channel.onmessage = (event: MessageEvent) => {
      if ((event.data as { type?: string } | null)?.type !== "mcp-oauth-complete") return;
      finish("connected");
    };
  });
}
